import { OverflowTooltip } from '@/components/overflow-tooltip'
import { Table as BaseTable } from '@/components_deprecated'
import type { Column } from '@/components_deprecated/Table'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import { format } from '@/utils/date-fns'
import { useCompany, useModuleConfig, useNodes } from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import React, { useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import { Link } from 'react-router'
import styled, { css, useTheme } from 'styled-components'
import { USER_TIME_ENTRIES_QUERY } from '../queries'
import type { UserTimeEntryNode } from '../types.graphql'
import { OldDateCell } from './table-cells'
import { useFormatTimeEntryLastAction } from './TimesheetTableRow/helpers/latestAction'
import { ViewCommentModal } from './ViewCommentModal'
import { ViewTimeEntryLogsModal } from './view-time-entry-logs-modal'

const Wrapper = styled.div`
  margin-top: 2rem;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin: 1rem 0 0 8px;

  & > h2 {
    font-weight: 600;
  }
`

const ShowAllLink = styled(Link)`
  font-weight: 600;
  color: ${props => props.theme.colors.secondary};
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

const Table = styled(BaseTable)`
  position: relative;
  margin-top: 1rem;

  ${props => props.theme.media.largePad}
  ${props => props.theme.media.mobile} {
    overflow: auto;
  }
`

const Row = styled.tr`
  td {
    box-sizing: content-box;
    height: 30px;
  }
`

const LastActionCell = styled.td<{ clickable?: boolean }>`
  ${props =>
    props.clickable &&
    css`
      cursor: pointer;
    `}

  overflow: hidden;
`

const LastActionTextStyled = styled.span`
  display: inline-block;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`

const IconContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`

const StyledIcon = styled(Icon)`
  cursor: pointer;
`

const LastActionText: React.FC<{ children: string }> = ({ children }) => {
  if (!children) return <LastActionTextStyled />

  return (
    <OverflowTooltip text={children}>
      <LastActionTextStyled>{children}</LastActionTextStyled>
    </OverflowTooltip>
  )
}

const hasVisibleComment = (comment?: string | null) => {
  if (!comment) return false

  return !['Ingen kommentar', 'No comment'].includes(comment.trim())
}

interface TimesheetsTableProps {
  startDate: Date
  endDate: Date
  userId: string
}

export const SimpleTimesheetsTable: React.FC<TimesheetsTableProps> = ({
  startDate,
  endDate,
  userId,
}) => {
  const theme = useTheme()
  const company = useCompany()
  const addPrompt = usePrompt()
  const formatLastAction = useFormatTimeEntryLastAction()
  const translations = useTranslate({
    date: 'common.date',
    dayStart: 'common.day-start',
    dayEnd: 'common.day-end',
    ordinary: 'common.ordinary',
    hours: 'common.work-hours',
    salaryCode: 'common.salary-code',
    department: 'common.department',
    facility: 'common.facility',
    latestAction: 'common.latest-action',
    comment: 'common.comment',
    showAll: 'common.show-all',
    timeEntriesThisPeriod: 'timesheets.time-entries-this-period',
  })

  const { getModuleOptionStatus } = useModuleConfig(Module.TIMESHEETS)
  const projectActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_PROJECTS
  )
  // Salary code / department only fit on larger screens, mirroring TimeEntries.
  const showSalaryColumns = !isMobile

  const { data, loading } = useQuery(USER_TIME_ENTRIES_QUERY, {
    variables: {
      datetimeEndGt: startDate.toISOString(),
      datetimeEndLt: endDate.toISOString(),
      user: userId,
    },
  })

  const timeEntries = useNodes(data?.allTimeEntries) as UserTimeEntryNode[]

  const columns: Column[] = useMemo(() => {
    let cols: Column[] = [
      { name: 'date', displayName: translations.date, type: '' },
      { name: 'startTime', displayName: translations.dayStart, type: '' },
      { name: 'endTime', displayName: translations.dayEnd, type: '' },
      {
        name: 'totalWork',
        displayName: company.useOrdinaryHourCalculation
          ? translations.ordinary
          : translations.hours,
        type: '',
      },
    ]
    if (showSalaryColumns) {
      cols = [
        ...cols,
        { name: 'salaryCode', displayName: translations.salaryCode, type: '' },
        { name: 'department', displayName: translations.department, type: '' },
      ]
      if (projectActive) {
        cols = [
          ...cols,
          { name: 'project', displayName: translations.facility, type: '' },
        ]
      }
    }
    return [
      ...cols,
      {
        name: 'latestAction',
        displayName: translations.latestAction,
        type: '',
      },
      { name: 'comment', displayName: translations.comment, type: '' },
    ]
  }, [
    company.useOrdinaryHourCalculation,
    projectActive,
    showSalaryColumns,
    translations,
  ])

  const sortedEntries = useMemo(
    () =>
      [...timeEntries].sort(
        (left, right) =>
          new Date(left.datetimeStart).getTime() -
          new Date(right.datetimeStart).getTime()
      ),
    [timeEntries]
  )

  const viewLogs = (entry: UserTimeEntryNode) =>
    addPrompt<boolean | null>(resolve => (
      <ViewTimeEntryLogsModal timeEntryId={entry.id} onClose={resolve} />
    ))

  const viewComment = (comment: string) =>
    addPrompt<boolean | null>(resolve => (
      <ViewCommentModal comment={comment} onClose={resolve} />
    ))

  if (loading || sortedEntries.length === 0) return null

  return (
    <Wrapper>
      <HeaderRow>
        <h2>{translations.timeEntriesThisPeriod}</h2>
        <ShowAllLink
          to={`/time-entries/${userId}?month=${format(startDate, 'yyyy-MM-dd')}`}
        >
          {translations.showAll}
        </ShowAllLink>
      </HeaderRow>
      <Table headerMargin rowHoverBackground="gray10">
        <thead>
          <tr>
            {columns.map(column => (
              <th key={column.name}>{column.displayName || column.name}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedEntries.map(entry => {
            const latestAction = formatLastAction(entry.latestActionLog)

            return (
              <Row key={entry.id}>
                <td>{format(parseISO(entry.datetimeStart), 'dd. MMM')}</td>

                <OldDateCell
                  date={parseISO(entry.datetimeStart)}
                  side="start"
                  type="timeentry"
                />

                {entry.datetimeEnd ? (
                  <OldDateCell
                    date={parseISO(entry.datetimeEnd)}
                    side="end"
                    type="timeentry"
                  />
                ) : (
                  <td></td>
                )}

                <td>{entry.totalOrdinary ?? entry.totalWork ?? '-'}</td>

                {showSalaryColumns && (
                  <>
                    <td>{entry.salaryCodeOrdinary?.name ?? '-'}</td>
                    <td>{entry.department?.name ?? '-'}</td>
                    {projectActive && <td>{entry.facility?.name ?? '-'}</td>}
                  </>
                )}

                <LastActionCell
                  clickable={!!entry.latestActionLog}
                  onClick={e => {
                    e.stopPropagation()
                    if (entry.latestActionLog) {
                      void viewLogs(entry)
                    }
                  }}
                >
                  <LastActionText>{latestAction}</LastActionText>
                </LastActionCell>

                <td width="130px">
                  {hasVisibleComment(entry.comment) && (
                    <IconContainer>
                      <StyledIcon
                        color={theme.colors.gray1}
                        hoverColor={theme.colors.secondary}
                        icon="comment-alt-lines"
                        size="16px"
                        onClick={() => viewComment(entry.comment || '')}
                      />
                    </IconContainer>
                  )}
                </td>
              </Row>
            )
          })}
        </tbody>
      </Table>
    </Wrapper>
  )
}
